import { imageNames } from "./imageConstants";
import starIcon from "../assets/images/neutral/grey/star.svg";
import { isUUID } from "./crmConstants";

export const objectNames = [
  {
    label: "Singular",
    placeholder: "Enter object singular name",
    example: "Book",
  },
  {
    label: "Plural",
    placeholder: "Enter object plural name",
    example: "Books",
  },
];

export const objectFormRules = {
  singular: [
    {
      required: true,
      message: "Please enter Object name",
    },
  ],
  plural: [
    {
      required: true,
      message: "Please enter Object's plural name",
    },
  ],
};

export const objectConfigurationSections = [
  {
    id: "create_form",
    title: "Create entity form",
    description:
      "Add, remove, or edit fields on the 'Create {objectName}' form",
    buttonText: 'Customize "Create {objectName}" form',
    buttonIcon: imageNames.EDIT,
    action: "customizeCreateForm",
  },
  {
    id: "object_details",
    title: "Custom object details",
    description:
      "Edit the custom object details like object name & secondary property",
    buttonText: "Edit details",
    buttonIcon: imageNames.EDIT,
    action: "editObjectDetails",
  },
];

export const standardObjects = [
  {
    slug: "contacts",
    viewSlug: "contact",
    label: "Contacts",
    singular: "Contact",
    value: "contacts",
  },
  {
    slug: "leads",
    viewSlug: "lead",
    label: "Leads",
    singular: "Lead",
    value: "leads",
  },
  {
    slug: "companies",
    viewSlug: "company",
    label: "Companies",
    singular: "Company",
    value: "companies",
  },
  {
    slug: "deals",
    viewSlug: "deal",
    label: "Deals",
    singular: "Deal",
    value: "deals",
  },
];

export const standardObjectRouteNames = standardObjects.map((obj) => obj.slug);

export const associationSections = [
  {
    id: "source-to-target",
    formFieldName: "sourceCardinality",
    headerConfig: {
      sourceKey: "sourceObjectSingular",
      targetKey: "targetObjectPlural",
    },
    radioConfig: {
      targetObjectSingular: "targetObjectSingular",
      targetObjectPlural: "targetObjectPlural",
    },
    cardinalityOptions: [
      {
        value: "many",
        prefix: "Many",
        labelKey: "plural",
      },
      {
        value: "one",
        prefix: "One",
        labelKey: "singular",
      },
    ],
  },
  {
    id: "target-to-source",
    formFieldName: "targetCardinality",
    headerConfig: {
      sourceKey: "targetObjectSingular",
      targetKey: "sourceObjectPlural",
    },
    radioConfig: {
      targetObjectSingular: "sourceObjectSingular",
      targetObjectPlural: "sourceObjectPlural",
    },
    cardinalityOptions: [
      {
        value: "many",
        prefix: "Many",
        labelKey: "plural",
      },
      {
        value: "one",
        prefix: "One",
        labelKey: "singular",
      },
    ],
  },
];

export const associationTableHeaders = [
  {
    label: "Association Object",
    key: "associationObject",
    width: 3,
  },
  {
    label: "Limits",
    key: "limits",
    width: 3,
  },
];

export const cardinalityDisplayMap = {
  one_to_one: "One-to-One",
  one_to_many: "One-to-Many",
  many_to_one: "Many-to-One",
  many_to_many: "Many-to-Many",
};

export const getObjectPropertyActionOptions = ({
  onEdit,
  onMarkPrimary,
  onDelete,
} = {}) => [
  {
    label: "Mark as Primary Property",
    handler: onMarkPrimary,
    imgSrc: starIcon,
    condition: (row) =>
      !row?.is_primary && row?.typeValue === "text" && row?.workspace_id !== 0,
  },
  {
    label: "Edit",
    handler: onEdit,
    icon: imageNames.EDIT,
  },
  {
    label: "Delete",
    handler: onDelete,
    icon: imageNames.DELETE_GREY,
    condition: (row) => !row?.is_primary && row?.workspace_id !== 0 && !row?.is_system && isUUID(row?.id),
  },
];

export const scoreOptions = [
  { id: "high", name: "High" },
  { id: "medium", name: "Medium" },
  { id: "low", name: "Low" },
];

export const analyticsHardcodedSchemaObjects = {
  simple: [
    {
      id: "calls",
      slug: "calls",
      name: "Call",
      plural_name: "Calls",
      description: "Call management and tracking",
      default_sort: "created_at",
      icon: "phone",
      is_system: true,
      created_at: "2025-12-18T12:53:51.353977Z",
      updated_at: "2025-12-18T12:53:51.353977Z",
      created_by: 1,
      updated_by: 0,
    },
    {
      id: "sequences",
      slug: "sequences",
      name: "Sequence",
      plural_name: "Sequences",
      description:
        "Create and manage multi-step outreach sequences to automate follow-ups through the pipeline.",
      default_sort: "created_at",
      icon: "phone",
      is_system: true,
      created_at: "2025-12-18T12:53:51.353977Z",
      updated_at: "2025-12-18T12:53:51.353977Z",
      created_by: 1,
      updated_by: 0,
    },
    {
      id: "meetings",
      slug: "meetings",
      name: "Meeting",
      plural_name: "Meetings",
      description:
        "Schedule, manage, and track meetings with leads and customers to support deal progression.",
      default_sort: "created_at",
      icon: "phone",
      is_system: true,
      created_at: "2025-12-18T12:53:51.353977Z",
      updated_at: "2025-12-18T12:53:51.353977Z",
      created_by: 1,
      updated_by: 0,
    },
    {
      id: "emails",
      slug: "emails",
      name: "Email",
      plural_name: "Emails",
      description:
        "Send, manage, and track email conversations with leads and customers to support engagement and deal progression.",
      default_sort: "created_at",
      icon: "mail",
      is_system: true,
      created_at: "2025-12-18T12:53:51.353977Z",
      updated_at: "2025-12-18T12:53:51.353977Z",
      created_by: 1,
      updated_by: 0,
    },
  ],
  complex: [
    {
      id: "forms",
      slug: "forms",
      name: "Form",
      plural_name: "Forms",
      description:
        "Form creation, submission tracking, and response management",
      default_sort: "created_at",
      icon: "file-text",
      is_system: true,
      created_at: "2025-12-18T12:53:51.353977Z",
      updated_at: "2025-12-18T12:53:51.353977Z",
      created_by: 1,
      updated_by: 0,
    },
  ],
};

export const propertySlugs = {
  owner: "owner",
  user: "user",
  tags: "tags",
  country: "country",
  countries: "countries",
  team: "team",
  company: "company",
  companySize: "company_size",
  currentCompany: "current_company",
  industry: "industry",
  pipelineId: "pipeline_id",
  pipeline: "pipeline",
  pipelineStageId: "pipeline_stage_id",
  dealStage: "deal_stage",
  disposition: "disposition",
  priority: "priority",
  dealType: "deal_type",
  source: "source",
  score: "score",
};

export const objectSlugs = {
  deals: "deals",
  deal: "deal",
  calls: "calls",
  companies: "companies",
  leads: "leads",
};

// prevent the following keys from getting rendered as user dropdown
export const excludedUserSlugMatches = ["user_timezone", "auth_user_workspace_id"];
export const excludedTeamSlugMatches = ["agent_team_id"];

export const propertyTypes = {
  user: "user",
  boolean: "boolean",
  tags: "tags",
  relationship: "relationship",
  select: "select",
  multiSelect: "multi_select",
};
